import React from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { formatNotificationTime } from '../../utils/notifications';

const ICONS = {
    alert: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9" /><path d="M12 8v4" /><path d="M12 16h.01" /></svg>
    ),
    document: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round"><path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" /><path d="M14 3v5h5" /><path d="M9 13h6" /><path d="M9 17h6" /></svg>
    ),
    wrench: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round"><path d="M14.7 6.3a4 4 0 0 0-5.2 5.2L4 17v3h3l5.5-5.5a4 4 0 0 0 5.2-5.2l-2.6 2.6-2.1-.5-.5-2.1z" /></svg>
    ),
    calendar: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="17" rx="2" /><path d="M16 2v4" /><path d="M8 2v4" /><path d="M3 10h18" /><path d="m9 15 2 2 4-4" /></svg>
    ),
    dollar: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9" /><path d="M12 7v10" /><path d="M14.5 9.5a2.5 2 0 0 0-2.5-1.5c-1.4 0-2.5.8-2.5 2s1.1 1.7 2.5 2 2.5.8 2.5 2-1.1 2-2.5 2a2.5 2 0 0 1-2.5-1.5" /></svg>
    ),
    message: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H8l-5 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></svg>
    ),
};

const TONES = {
    red: { bubble: 'bg-[#fde8e8]', icon: 'text-[#dc2626]', accent: '#dc2626' },
    blue: { bubble: 'bg-[#F2F2F2]', icon: 'text-[#414141]', accent: '#D81F26' },
    orange: { bubble: 'bg-[#fef1d6]', icon: 'text-[#d97706]', accent: '#d97706' },
    green: { bubble: 'bg-[#e8fbef]', icon: 'text-[#15803d]', accent: '#15803d' },
    slate: { bubble: 'bg-[#f2f2f2]', icon: 'text-[#414141]', accent: '#a2a2a2' },
    amber: { bubble: 'bg-[#fef3c7]', icon: 'text-[#b45309]', accent: '#b45309' },
    teal: { bubble: 'bg-[#d9f7ef]', icon: 'text-[#0d9488]', accent: '#0d9488' },
};

function NotificationCard({ notification, onOpen, onDismiss }) {
    const tone = TONES[notification.tone] || TONES.slate;
    const isUnread = !notification.isRead;
    return (
        <div
            role="button"
            tabIndex={0}
            onClick={() => onOpen(notification)}
            onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    onOpen(notification);
                }
            }}
            className={`group relative w-full cursor-pointer overflow-hidden rounded-2xl border text-left shadow-sm transition-all duration-150 ${
                isUnread
                    ? 'border-[#d9d9d9] bg-[#f8f8f8] hover:bg-[#f5f5f5]'
                    : 'border-[#d9d9d9] bg-white hover:bg-[#F9F9F9]'
            }`}
        >
            {isUnread && (
                <span className="absolute inset-y-0 left-0 w-1.5" style={{ backgroundColor: tone.accent }} aria-hidden="true" />
            )}
            <div className="flex items-start gap-3.5 px-4 py-4 pl-5 md:px-5 md:pl-6">
                <div className={`mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${tone.bubble} ${tone.icon}`}>
                    {ICONS[notification.icon] || ICONS.alert}
                </div>
                <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                        <h3 className="text-[15px] font-black tracking-tight text-[#414141]">{notification.title}</h3>
                        {isUnread ? (
                            <span className="rounded-full bg-[#414141] px-2.5 py-0.5 text-[10px] font-black uppercase tracking-wide text-white">Unread</span>
                        ) : (
                            <span className="rounded-full bg-[#f2f2f2] px-2.5 py-0.5 text-[10px] font-black uppercase tracking-wide text-[#414141]">Read</span>
                        )}
                    </div>
                    <p className="mt-1.5 text-[14px] font-medium leading-relaxed text-[#565656]">{notification.message}</p>
                    <p className="mt-2.5 text-[12px] font-semibold text-[#939393]">
                        {notification.category} / {formatNotificationTime(notification.timestamp)}
                    </p>
                </div>
                <div className="mt-0.5 flex shrink-0 items-center gap-1">
                    <button
                        type="button"
                        onClick={(event) => {
                            event.stopPropagation();
                            onDismiss(notification.id);
                        }}
                        className="flex h-7 w-7 items-center justify-center rounded-lg text-[#c3c3c3] opacity-100 transition-all hover:bg-black/5 hover:text-[#c0392b] md:opacity-0 md:group-hover:opacity-100"
                        title="Delete notification"
                        aria-label="Delete notification"
                    >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18" /><path d="M8 6V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2" /><path d="M19 6l-1 14a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1L5 6" /></svg>
                    </button>
                    <svg className="text-[#c3c3c3] transition-colors group-hover:text-[#414141]" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="m9 18 6-6-6-6" />
                    </svg>
                </div>
            </div>
        </div>
    );
}

export default function ShopNotificationsPage() {
    const {
        notifications = [],
        markNotificationRead,
        markAllNotificationsRead,
        dismissNotification,
    } = useOutletContext() || {};
    const navigate = useNavigate();
    const unreadCount = notifications.reduce((total, item) => (item.isRead ? total : total + 1), 0);

    const openNotification = (notification) => {
        if (!notification.isRead && markNotificationRead) markNotificationRead(notification.id);
        const link = notification.link;
        if (link && typeof link === 'string' && link.startsWith('/')) {
            navigate(link);
        }
    };

    return (
        <div className="h-full w-full overflow-y-auto bg-[#F9F9F9] custom-scrollbar relative">
            <div className="mx-auto w-full max-w-3xl px-4 pt-8 pb-12 md:px-6 md:pt-10">
                <header className="mb-7 flex flex-wrap items-start justify-between gap-4 animate-in fade-in slide-in-from-top-4 duration-700">
                    <div>
                        <h1 className="text-2xl md:text-[28px] font-black tracking-tight text-black">Notifications</h1>
                        <p className="mt-1.5 text-[15px] font-medium text-[#414141]">
                            {unreadCount > 0 ? `${unreadCount} unread` : 'All caught up'}
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={() => markAllNotificationsRead && markAllNotificationsRead()}
                        disabled={unreadCount === 0}
                        className={`flex items-center gap-2 rounded-full border px-4 py-2 text-[13px] font-bold transition-colors ${
                            unreadCount === 0
                                ? 'cursor-default border-[#d9d9d9] bg-white text-[#c3c3c3]'
                                : 'border-[#d9d9d9] bg-white text-[#414141] shadow-sm hover:bg-[#f5f5f5]'
                        }`}
                    >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><path d="m2 12 5 5L18 6" /><path d="m13 17 1 1 8-9" /></svg>
                        Mark all as read
                    </button>
                </header>

                {notifications.length === 0 ? (
                    <div className="rounded-2xl border border-[#d9d9d9] bg-white px-6 py-16 text-center shadow-sm">
                        <p className="text-[15px] font-semibold text-[#414141]">No notifications yet.</p>
                    </div>
                ) : (
                    <div className="flex flex-col gap-3">
                        {notifications.map((notification) => (
                            <NotificationCard
                                key={notification.id}
                                notification={notification}
                                onOpen={openNotification}
                                onDismiss={dismissNotification}
                            />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
